
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { useSimilarProperties } from "@/hooks/useSimilarProperties";
import { useBuyer } from "@/contexts/BuyerContext";

interface SimilarPropertiesSectionProps {
  currentPropertyId: string;
  city: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  propertyType?: string | null;
}

export const SimilarPropertiesSection = ({
  currentPropertyId,
  city,
  price,
  bedrooms,
  bathrooms,
  propertyType
}: SimilarPropertiesSectionProps) => {
  const { isBuyer } = useBuyer();
  const { similarProperties, isLoading, error } = useSimilarProperties({
    currentPropertyId,
    city,
    price,
    bedrooms,
    bathrooms,
    propertyType
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const createdDate = new Date(dateString);
    const diffInMs = now.getTime() - createdDate.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return '1 day ago';
    return `${diffInDays} days ago`;
  };

  // Determine the correct route based on user type
  const getPropertyRoute = (propertyId: string) => {
    return isBuyer ? `/buyer/properties/${propertyId}` : `/seller/properties/${propertyId}`;
  };

  if (error) {
    return (
      <div>
        <h3 className="font-semibold mb-3">Similar Properties</h3>
        <p className="text-sm text-muted-foreground">Unable to load similar properties</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="font-semibold mb-3">Similar Properties</h3>
      
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="overflow-hidden">
              <div className="flex h-24">
                <div className="w-1/3 bg-muted animate-pulse" />
                <div className="w-2/3 p-2 space-y-2">
                  <div className="h-4 bg-muted animate-pulse rounded" />
                  <div className="h-3 bg-muted animate-pulse rounded w-2/3" />
                  <div className="h-4 bg-muted animate-pulse rounded w-1/2" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : similarProperties.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground">No similar properties found in {city}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {similarProperties.map((property) => (
            <Link key={property.id} to={getPropertyRoute(property.id)}>
              <Card className="overflow-hidden hover-scale cursor-pointer">
                <div className="flex h-24">
                  <div className="w-1/3">
                    <img
                      src={property.image_url || "https://images.unsplash.com/photo-1570129477492-45c003edd2be"}
                      alt={`Property at ${property.address}`}
                      className="object-cover h-full w-full"
                    />
                  </div>
                  <div className="w-2/3 p-2 flex flex-col justify-between">
                    <div>
                      <p className="font-medium truncate text-sm">{property.address}</p>
                      <p className="text-xs text-muted-foreground">{property.city}, {property.state}</p>
                      <p className="text-xs text-muted-foreground">
                        {property.bedrooms} beds • {property.bathrooms} baths • {property.square_feet.toLocaleString()} sq ft
                      </p>
                      {(property.seller_name || property.seller_business) && (
                        <p className="text-xs text-muted-foreground truncate">
                          Listed by {property.seller_business || property.seller_name}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <p className="font-bold text-godyspo-gold text-sm">{formatCurrency(property.price)}</p>
                      <p className="text-xs text-muted-foreground">{formatTimeAgo(property.created_at)}</p>
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
